import React, { useMemo, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import useEventListener from '../../hooks/useEventListener.hooks';
import { selectIsIssueReportingEnabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: Shortcut[];
}

// Single source of truth for the agent-facing shortcut legend. Mirrors the
// handlers actually wired in the HITL 3.0 layout — keep in sync when adding or
// changing a shortcut.
const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    title: 'Speak',
    shortcuts: [{ keys: ['Ctrl'], description: 'Hold to talk (push-to-talk)' }],
  },
  {
    title: 'Intents',
    shortcuts: [
      { keys: ['Alt', '1–8'], description: 'Send the numbered intent' },
      { keys: ['↑'], description: 'Okay' },
      { keys: ['←'], description: 'Moment' },
      { keys: ['→'], description: 'Continue' },
      { keys: ['↓'], description: 'Read Back' },
    ],
  },
  {
    title: 'AI suggestion',
    shortcuts: [
      { keys: ['Alt', 'A'], description: 'Accept the current suggestion' },
      { keys: ['Alt', 'D'], description: 'Dismiss the current suggestion' },
    ],
  },
  {
    title: 'Cart & items',
    shortcuts: [
      { keys: ['Shift', '↑'], description: 'Select previous cart item' },
      { keys: ['Shift', '↓'], description: 'Select next cart item' },
      { keys: ['Shift', 'Backspace'], description: 'Remove selected item' },
      { keys: ['Shift', 'W'], description: 'Previous modifier group' },
      { keys: ['Shift', 'S'], description: 'Next modifier group' },
      { keys: ['Shift', 'D'], description: 'Enter nested modifier group' },
      { keys: ['Shift', 'A'], description: 'Back to parent group' },
    ],
  },
  {
    title: 'Other',
    shortcuts: [
      { keys: ['Enter'], description: 'Send typed message' },
      { keys: ['Shift', '←'], description: 'Expand / collapse banner' },
      { keys: ['Alt', '/'], description: 'Open / close this help' },
    ],
  },
];

// HITL-1499. Appended only when issue reporting is on — advertising Alt+I while
// the feature is off would be a lie. Its own trailing section rather than a row on
// "Other" because HITL-1500 (quick-pick digits) and HITL-1501 add rows to it.
const ISSUE_REPORTING_SECTION: ShortcutSection = {
  title: 'Report an issue',
  shortcuts: [
    { keys: ['Alt', 'I'], description: 'Flag an issue with this order' },
  ],
};

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: HITL3_FONT_SIZE,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  keys: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  kbd: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    borderRadius: '4px',
    border: '1px solid #c4c4c4',
    borderBottomWidth: 2,
    backgroundColor: '#f7f7f7',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 600,
    fontFamily: 'monospace',
    color: '#333',
  },
  desc: {
    fontSize: '0.85rem',
  },
}));

/**
 * "?" affordance + shortcuts legend for the HITL 3.0 layout. Self-contained:
 * renders its own header trigger button and owns the Alt+/ toggle. Gating to
 * hitl3 is the caller's responsibility (see HeaderIndicators).
 */
const KeyboardShortcutsHelp = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  // SHORTCUT_SECTIONS is a hand-maintained module constant, so the flag-aware
  // part has to be composed at render time.
  const isIssueReportingEnabled = useSelector(selectIsIssueReportingEnabled);
  const sections = useMemo(
    () =>
      isIssueReportingEnabled
        ? [...SHORTCUT_SECTIONS, ISSUE_REPORTING_SECTION]
        : SHORTCUT_SECTIONS,
    [isIssueReportingEnabled]
  );

  // Alt+/ toggles the legend. `/` is reported as code "Slash" regardless of
  // layout; preventDefault keeps Alt+/ from doing anything else.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (
      e.altKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.shiftKey &&
      e.code === 'Slash'
    ) {
      e.preventDefault();
      e.stopPropagation();
      setOpen((prev) => !prev);
    }
  };
  // Capture phase so the focused omnibar can't swallow Alt+/ first.
  useEventListener('keydown', handleKeyDown, window, true);

  return (
    <>
      <Tooltip title="Keyboard shortcuts (Alt+/)">
        <IconButton
          // sx (high-priority emotion class) beats MUI's IconButton baseline
          // colour, which a makeStyles class can't reliably override. Matches the
          // button yellow (#FFC107) so it reads as an affordance on the header.
          // Sized to the same 32px box as the other header indicators.
          sx={{ color: Colors.amber, width: 32, height: 32, p: 0 }}
          aria-label="Keyboard shortcuts"
          onClick={() => setOpen(true)}
        >
          <HelpOutlineIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Keyboard shortcuts</DialogTitle>
        <DialogContent dividers>
          {sections.map((section) => (
            <Box key={section.title} className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {section.title}
              </Typography>
              {section.shortcuts.map((shortcut) => (
                <div key={shortcut.description} className={classes.row}>
                  <span className={classes.desc}>{shortcut.description}</span>
                  <span className={classes.keys}>
                    {shortcut.keys.map((key, i) => (
                      <kbd
                        key={`${shortcut.description}-${i}`}
                        className={classes.kbd}
                      >
                        {key}
                      </kbd>
                    ))}
                  </span>
                </div>
              ))}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KeyboardShortcutsHelp;
